import { WorkflowName } from "../../../apis/API";
import {
  AdaptiveConfirmConfig,
  BranchOpensOn,
  OnFileListConfirm,
  RepeatableSerialization,
  WorkflowField,
  WorkflowSpec,
  WorkflowTemplate,
} from "./types";

/** Build an ordered field list (all required by default). */
const fields = (...keys: string[]): WorkflowField[] =>
  keys.map((fieldKey) => ({ fieldKey, required: true }));

/**
 * Build an ordered field list where `optionalKeys` are marked `required:false`.
 * Only basic-knowledge fields are required (confirmed by Callie 2026-07-01); the
 * rest are optional so a blank field becomes an officer talking point rather than
 * blocking the participant.
 */
const fieldsOpt = (keys: string[], optionalKeys: string[]): WorkflowField[] =>
  keys.map((fieldKey) => ({
    fieldKey,
    required: !optionalKeys.includes(fieldKey),
  }));

const spec = (
  template: WorkflowTemplate,
  opts: {
    repeatable?: boolean;
    branchOpensOn?: BranchOpensOn;
    fields: WorkflowField[];
    /** Repeatable-only; see {@link RepeatableSerialization}. Omit for the default `"columnar"`. */
    serializeAs?: RepeatableSerialization;
    /** CONFIRM_ON_FILE-only adaptive confirm-or-collect config; see {@link AdaptiveConfirmConfig}. */
    adaptive?: AdaptiveConfirmConfig;
    /** Repeatable structured-form on-file-list confirm config; see {@link OnFileListConfirm}. */
    onFileListConfirm?: OnFileListConfirm;
  },
): WorkflowSpec => ({
  template,
  repeatable: opts.repeatable ?? false,
  branchOpensOn: opts.branchOpensOn ?? "YES",
  fields: opts.fields,
  serializeAs: opts.serializeAs,
  adaptive: opts.adaptive,
  onFileListConfirm: opts.onFileListConfirm,
});

/** Single-field repeatable list spec (YES_NO_REPEATABLE_LIST). */
const repeatableList = (fieldKey: string): WorkflowSpec =>
  spec("YES_NO_REPEATABLE_LIST", { repeatable: true, fields: fields(fieldKey) });

/**
 * Yes/No → free-text spec (YES_NO_FREE_TEXT). The single field is keyed `value`
 * to match the cross-client `workflowAnswers` contract (source-of-truth
 * "Workflow answer response format": Yes → `{"value": "<text>"}`, No → `{}`).
 */
const freeText = (): WorkflowSpec =>
  spec("YES_NO_FREE_TEXT", { fields: fields("value") });

/** Workflows that share the single `value` repeatable-list spec. */
const PROGRAM_LIST_WORKFLOWS = [
  "treatmentPrograms",
  "naAa",
  "mentalHealthTreatment",
  "sexOffenderTreatment",
  "communityService",
] as const;

/** Workflows that share the Yes/No free-text (`value`) spec. */
const FREE_TEXT_WORKFLOWS = [
  // general
  "planAddressChange",
  "householdChange",
  "planPhoneChange",
  "employmentIssues",
  "schoolIssues",
  "lawEnforcement",
  "problemsDiscuss",
  "falseClaims",
  "childSupport",
  "feesRestitution",
  "equipmentIssues",
  // sex-offender behavioral set
  "soChildContact",
  "soAngerFemales",
  "soViolent",
  "soAttracted",
  "soFollowing",
  "soMasturbated",
  "soDeviantFantasies",
  "soSexualContact",
  "soPornography",
  "soInternet",
  "soViolatedSupervision",
  "soSubstances",
  "soAssociatedUsers",
  "soEmotions",
  "soHurting",
  "soOutOfControl",
  "soLostRelationships",
  "soNewFriends",
  "soSafetyNet",
  "soChurch",
] as const;

const fromList = <K extends string>(
  names: readonly K[],
  make: () => WorkflowSpec,
): Record<K, WorkflowSpec> =>
  names.reduce(
    (acc, name) => {
      acc[name] = make();
      return acc;
    },
    {} as Record<K, WorkflowSpec>,
  );

/**
 * The full workflow registry — `workflowName` -> {@link WorkflowSpec}.
 *
 * Typed as `Record<WorkflowName, WorkflowSpec>` so TypeScript enforces that all
 * 49 workflow names are present (a missing entry is a compile error). Ported
 * from the Android `WorkflowRegistry`.
 */
export const WORKFLOW_REGISTRY: Record<WorkflowName, WorkflowSpec> = {
  // CONFIRM_ON_FILE
  // `country` is carried through from on-file (not an editable input) but is part
  // of the serialized answer — matches Android V3AnswerAssembler (5 fields).
  addressConfirmation: spec("CONFIRM_ON_FILE", {
    branchOpensOn: "CHANGED",
    fields: fields("streetAddress", "city", "state", "zipCode", "country"),
  }),
  // Two fixed fields (one number of each type), not a repeatable list. Keys are
  // mobile/other (the backend prepopulation shape); displayed as "Main"/"Other"
  // per Figma Phone 1.1/1.2.
  // Adaptive confirm-or-collect (EHA-861/IHA-407): confirm flow ("Has your
  // phone number changed…?", the server prompt) when a `mobile` OR `other` is on
  // file — echoing `changed:false`/`true`; else an ask flow ("Do you have any
  // phone numbers?") that collects with no `changed` key. `other` is optional
  // (only `mobile` is required), matching the Android contract.
  phoneConfirmation: spec("CONFIRM_ON_FILE", {
    branchOpensOn: "CHANGED",
    fields: fieldsOpt(["mobile", "other"], ["other"]),
    adaptive: {
      qualifyingFields: ["mobile", "other"],
      confirmStepId: "root",
      askHeadlineKey: "askRoot",
    },
  }),
  appointment: spec("CONFIRM_ON_FILE", { branchOpensOn: "YES", fields: [] }),
  // Adaptive confirm-or-collect (IHA-395/EHA-908). Confirm flow (on-file email
  // exists): Correct/Changed root (confirm headline from catalog
  // `hasEmailChanged`), echoing `changed:false`/`true`. Ask flow (no on-file
  // value): the server prompt "Do you have an email address?" collects with NO
  // `changed` key. Matches the agency `ConfirmOrCollectWorkflowAnswer`, which
  // discriminates the two shapes by whether `changed` is present.
  email: spec("CONFIRM_ON_FILE", {
    branchOpensOn: "YES",
    fields: fields("email"),
    adaptive: {
      qualifyingFields: ["email"],
      confirmStepId: "rootEmailPresent",
      confirmHeadlineKey: "hasEmailChanged",
    },
  }),
  // Adaptive confirm-or-collect (EHA-861/IHA-407). Confirm flow ("Have there
  // been changes to your emergency contact's information?", the server prompt)
  // when an on-file `phone` exists — an on-file `name` alone never qualifies;
  // else an ask flow ("Do you have an emergency contact?"). Both `name` and
  // `phone` are required in the entry form (Callie 2026-07-01).
  emergencyContact: spec("CONFIRM_ON_FILE", {
    branchOpensOn: "CHANGED",
    fields: fields("name", "phone"),
    adaptive: {
      qualifyingFields: ["phone"],
      confirmStepId: "root",
      askHeadlineKey: "askRoot",
    },
  }),

  // YES_NO_STRUCTURED_FORM
  // Required-field policy per Callie (2026-07-01, #discuss-questions-forms):
  // only basic-knowledge fields are required; the rest are optional so blank
  // fields become officer talking points rather than blocking the participant.
  // Repeatable object-list: the officer-side renderer reads
  // `workflowAnswers.employment` as an array of per-job objects, so employment
  // serializes the same way as `vehicles` (a nested array under the workflow
  // key, not columnar parallel arrays).
  //
  // `onFileListConfirm`: employment also loads on-file jobs from prepopulation
  // and, when present, asks "Have there been changes to your jobs?" (reconfirm
  // vs replace) before the entry form — the dedicated EmploymentForm. A lone
  // on-file job opens a blank edit list rather than prefilling (likely stale).
  employment: spec("YES_NO_STRUCTURED_FORM", {
    repeatable: true,
    serializeAs: "objectList",
    onFileListConfirm: { prefillWhenMoreThanOne: true },
    fields: fieldsOpt(
      ["employerName", "jobTitle", "supervisorName", "streetAddress", "city", "state", "zipCode"],
      ["jobTitle", "supervisorName", "streetAddress", "city", "state", "zipCode"],
    ),
  }),
  // Object-list: the officer-side schema wants a nested array of per-vehicle
  // objects wrapped under `vehicles`, not columnar parallel arrays.
  vehicles: spec("YES_NO_STRUCTURED_FORM", {
    repeatable: true,
    serializeAs: "objectList",
    fields: fieldsOpt(
      ["year", "make", "model", "color", "plateNum", "ownerName", "insurance"],
      ["plateNum", "ownerName", "insurance"],
    ),
  }),
  travel: spec("YES_NO_STRUCTURED_FORM", {
    fields: fieldsOpt(["date", "location", "reason"], ["date", "location"]),
  }),
  feesPayment: spec("YES_NO_STRUCTURED_FORM", {
    fields: fieldsOpt(["amount", "date"], ["amount", "date"]),
  }),
  officerReporting: spec("YES_NO_STRUCTURED_FORM", {
    branchOpensOn: "ALWAYS",
    fields: fieldsOpt(
      ["officerName", "courtCase", "reason"],
      ["officerName", "courtCase", "reason"],
    ),
  }),

  // YES_NO_REPEATABLE_LIST
  medications: repeatableList("value"),
  school: repeatableList("value"),
  ...fromList(PROGRAM_LIST_WORKFLOWS, () => repeatableList("value")),

  // DIRECT_LIST
  householdMembers: spec("DIRECT_LIST", {
    repeatable: true,
    branchOpensOn: "ALWAYS",
    fields: fields("value"),
  }),

  // YES_NO_FREE_TEXT
  ...fromList(FREE_TEXT_WORKFLOWS, freeText),
};

/**
 * Resolve a `workflowName` to its spec. Returns `null` for any name not
 * explicitly registered — including inherited Object properties like
 * `constructor`/`toString`/`__proto__` — so unknown workflows are skipped
 * rather than matching a prototype member.
 */
export const lookupWorkflow = (
  workflowName: string | null | undefined,
): WorkflowSpec | null => {
  if (
    !workflowName ||
    !Object.prototype.hasOwnProperty.call(WORKFLOW_REGISTRY, workflowName)
  ) {
    return null;
  }
  return WORKFLOW_REGISTRY[workflowName as WorkflowName];
};
